import dayjs from "dayjs";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface DiaryStat {
    created_at: string;
    active_diaries: number;
    pending_diaries: number;
    inactive_diaries: number;
}

interface DiariesByTabSparkChartProps {
    diaryBreakdown: DiaryStat[];
}

function DiariesByTabSparkChart({ diaryBreakdown }: DiariesByTabSparkChartProps) {
    const chartData = diaryBreakdown.map((stat) => ({
        date: new Date(stat.created_at).getTime(),
        active: stat.active_diaries,
        pending: stat.pending_diaries,
        inactive: stat.inactive_diaries,
    }));

    const lastDiary = diaryBreakdown[diaryBreakdown.length - 1];
    const activeDiaries = lastDiary?.active_diaries ?? 0;
    const pendingDiaries = lastDiary?.pending_diaries ?? 0;
    const inactiveDiaries = lastDiary?.inactive_diaries ?? 0;
    const totalDiaries = activeDiaries + pendingDiaries + inactiveDiaries;

    return (
        <div className="flex flex-col justify-between w-full h-full">
            <div>
                <div className="font-bold text-2xl">{totalDiaries} Diaries</div>
                <div className="flex gap-4 text-sm">
                    <span className="text-blue-600">{activeDiaries} Active</span>
                    <span className="text-cyan-600">{pendingDiaries} Pending</span>
                    <span className="text-zinc-600">{inactiveDiaries} Inactive</span>
                </div>
            </div>
            <div className="w-full h-24">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                        <XAxis dataKey="date" hide />
                        <YAxis hide />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const data = payload[0].payload;
                                return (
                                    <div className="bg-white shadow-sm p-2 border border-slate-200 rounded-md text-sm">
                                        <div className="mb-2 font-medium">{dayjs(data.date).format("DD MMM YYYY")}</div>
                                        <div className="space-y-1">
                                            <div className="text-blue-600">Active: {data.active}</div>
                                            <div className="text-cyan-600">Pending: {data.pending}</div>
                                            <div className="text-zinc-600">Inactive: {data.inactive}</div>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey="active"
                            stackId="1"
                            stroke="hsl(217 91% 60%)"
                            fill="hsl(217 91% 60% / 0.2)"
                            strokeWidth={2}
                        />
                        <Area
                            type="monotone"
                            dataKey="pending"
                            stackId="1"
                            stroke="hsl(189 94% 43%)"
                            fill="hsl(189 94% 43% / 0.2)"
                            strokeWidth={2}
                        />
                        <Area
                            type="monotone"
                            dataKey="inactive"
                            stackId="1"
                            stroke="hsl(240 5% 65%)"
                            fill="hsl(240 5% 65% / 0.2)"
                            strokeWidth={2}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default DiariesByTabSparkChart;
